import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Loader2, CheckCircle2, CreditCard, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function SplitPay() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [payerName, setPayerName] = useState("");
  const [tipPct, setTipPct] = useState(0);
  const [customTip, setCustomTip] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/split/${bookingId}`);
      setInfo(data);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("cancelled")) toast.info("Payment cancelled — no charge was made.");
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setVerifying(false); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setVerifying(false);
          toast.success("Your share is paid — thank you!");
          load();
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setVerifying(false);
      }
    };
    poll();
  }, [params, load]);

  const payShare = async () => {
    if (!payerName.trim()) return toast.error("Please enter your name so the group knows who paid.");
    setPaying(true);
    try {
      const { data } = await api.post(`/split/${bookingId}/checkout`, { origin_url: window.location.origin, payer_name: payerName.trim(), tip: tipAmount });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment.");
      setPaying(false);
    }
  };

  if (loadState === "loading") {
    return (
      <section className="max-w-lg mx-auto px-5 py-28 text-center">
        <Loader2 className="h-12 w-12 text-gold mx-auto animate-spin" />
      </section>
    );
  }

  if (loadState === "error" || !info) {
    return (
      <section className="max-w-lg mx-auto px-5 py-28 text-center" data-testid="split-not-found">
        <h1 className="font-heading text-2xl font-bold text-white">Split link not found</h1>
        <p className="text-slate-400 mt-3">This booking may not exist or the link has expired.</p>
        <button onClick={() => navigate("/")} className="mt-8 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy hover:bg-gold-light transition-colors duration-200">Back to home</button>
      </section>
    );
  }

  const split = info.split || {};
  const enabled = split.enabled;
  const balance = info.balance_due;
  const share = split.share_amount || 0;
  const sharesPaid = split.shares_paid || 0;
  const shares = split.shares || 0;
  const settled = balance <= 0.01;
  const allSharesPaid = shares > 0 && sharesPaid >= shares;
  const payAmt = Math.min(share, balance);
  const paidBy = split.paid_by || [];
  const tipAmount = customTip !== "" ? Math.max(+customTip || 0, 0) : +(payAmt * tipPct / 100).toFixed(2);
  const chargedNow = +(payAmt + tipAmount).toFixed(2);

  return (
    <section className="max-w-xl mx-auto px-5 sm:px-8 py-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-sm bg-navy-light border border-gold/30 p-8 sm:p-10" data-testid="split-pay-page">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-6 w-6 text-gold" />
          <span className="text-sm uppercase tracking-[0.2em] text-gold">Split the bill</span>
        </div>
        <h1 className="font-heading text-3xl font-bold text-white">Pay your share</h1>
        <p className="text-slate-400 mt-2 text-sm">{info.name}'s trip — {info.description}</p>

        <div className="mt-7 rounded-sm bg-navy border border-gold/20 p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Date & time</span><span className="text-white">{info.date} at {info.time}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Total fare</span><span className="text-white">${info.amount.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Paid so far</span><span className="text-white">${info.amount_paid.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between border-t border-gold/20 pt-2 mt-2"><span className="text-slate-300 font-medium">Balance remaining</span><span className="text-gold font-bold" data-testid="split-balance">${balance.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Reference</span><span className="text-white">{info.reference}</span></div>
        </div>

        {settled ? (
          <div className="mt-7 text-center rounded-sm bg-gold/10 border border-gold/40 p-8" data-testid="split-settled">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white">This trip is fully paid!</h2>
            <p className="text-slate-400 mt-2 text-sm">Thanks — everyone's share has been collected.</p>
          </div>
        ) : enabled ? (
          <>
            <div className="mt-7">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300">Shares paid</span>
                <span className="text-sm font-semibold text-gold" data-testid="split-progress">{sharesPaid} of {shares}</span>
              </div>
              <div className="h-2 rounded-full bg-navy overflow-hidden border border-gold/20">
                <div className="h-full bg-gold transition-all duration-500" style={{ width: `${shares ? Math.min((sharesPaid / shares) * 100, 100) : 0}%` }} />
              </div>
              {paidBy.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="split-paid-by">
                  {paidBy.map((p, i) => (
                    <li key={i} className="flex justify-between text-xs text-slate-400">
                      <span className="text-slate-300"><CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-400 mr-1" />{p.name}</span>
                      <span>${Number(p.amount).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 flex items-end justify-between rounded-sm bg-navy border border-gold/20 p-5">
              <div>
                <p className="text-sm text-slate-400">Your share</p>
                <p className="font-heading text-3xl font-bold text-gold" data-testid="split-your-share">${payAmt.toFixed(2)}</p>
              </div>
              <span className="text-xs text-slate-500">{info.currency}</span>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Your name</label>
              <input value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="So your group knows who paid" data-testid="split-payer-name"
                className="w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200" />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Add a tip for your driver <span className="text-slate-500">(optional)</span></label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 10, 15, 20].map((p) => (
                  <button type="button" key={p} onClick={() => { setTipPct(p); setCustomTip(""); }} data-testid={`split-tip-${p}`}
                    className={`rounded-sm border px-2 py-2.5 text-sm font-medium transition-colors duration-200 ${customTip === "" && tipPct === p ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>
                    {p === 0 ? "None" : `${p}%`}
                  </button>
                ))}
              </div>
              {chargedNow !== payAmt && <div className="flex justify-between text-sm mt-3"><span className="text-slate-300 font-medium">You'll be charged</span><span className="font-semibold text-gold" data-testid="split-charged">${chargedNow.toFixed(2)}</span></div>}
            </div>

            <button onClick={payShare} disabled={paying || verifying || allSharesPaid} data-testid="split-pay-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
              {paying ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : verifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming payment…</> : <><CreditCard className="h-5 w-5" /> Pay my share by card</>}
            </button>

            <div className="mt-6 rounded-sm bg-navy border border-gold/20 p-5" data-testid="split-etransfer">
              <div className="flex items-center gap-2 mb-2">
                <Send className="h-4 w-4 text-gold" />
                <h3 className="font-heading text-sm font-bold text-white">Prefer Interac e-Transfer?</h3>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">Send <span className="text-gold font-semibold">${payAmt.toFixed(2)} {info.currency}</span> to <span className="text-gold font-semibold break-all">{info.etransfer_email}</span> (auto-deposit). Add reference <span className="text-gold font-semibold">{info.reference}</span> in the message.</p>
            </div>
          </>
        ) : (
          <p className="mt-7 text-sm text-slate-400" data-testid="split-disabled">Splitting hasn't been enabled for this booking yet.</p>
        )}

        <button onClick={() => navigate("/")} className="mt-7 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors duration-200">
          Back to home <ArrowRight className="h-4 w-4" />
        </button>
      </motion.div>
    </section>
  );
}
